// import { error } from 'node:console';

import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import {
	INVESTOR_TEST_ELEMENT_ID,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { CssValue } from '@dealstack/shared/lib/frontend/common-ui/types';
import { em } from '@mantine/core';
import { debounce } from 'lodash';
import React, {
	BaseSyntheticEvent,
	MutableRefObject,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { FieldError, RegisterOptions, useController } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';
import { PredefinedTextVariants } from '@dealstack/frontend/shared/components/fundament/Text/Text.types';
import { PredefinedV2TextVariants } from '@dealstack/frontend/shared/components/TextV2/TextV2.types';
import type { StringInputV2SizeValues } from './StringInput.v2.types';

interface Props {
	className?: string;
	onInputChange: (input: string | number) => void;
	label?: React.ReactNode | string;
	hint?: React.ReactNode | string;
	hintIcon?: React.ReactNode | PredefinedIcons;
	leftIcon?: React.ReactNode | PredefinedIcons;
	rightIcon?: React.ReactNode | PredefinedIcons;
	hotkeyComponent?: React.ReactNode;
	inputType?: string;
	disabled?: boolean;
	size?: StringInputV2SizeValues;
	inputPlaceHolder?: string;
	latestInputValue?: string | number;

	readOnly?: boolean;
	name: string;
	rhfOptions?: RegisterOptions;
	blockRedundantSpaces?: boolean;
	autocompleteEnabled?: boolean;
	onClick?: () => void;
	'data-testid'?: string;
	inputRef?: MutableRefObject<HTMLInputElement | null>;
}

export enum StringInputV2Size {
	small = 'small',
	medium = 'medium',
	s = 's',
	m = 'm',
	l = 'l',
	search = 'search',
}

type ISizeWithTextVariantMapping = {
	[key in StringInputV2SizeValues]: {
		labelTextVariant: PredefinedV2TextVariants;
		inputIconSize: {
			width: CssValue;
			height: CssValue;
		};
		hintIconSize: {
			width: CssValue;
			height: CssValue;
		};
		searchSelectTextBox: {
			gap: CssValue;
			padding: CssValue;
			fontSize: CssValue;
		};
		inputFieldHeight: CssValue;
		hint: { gap: CssValue; marginTop: CssValue };
		label: {
			marginBottom: CssValue;
		};
		leftIconInputPadding: {
			paddingLeft: CssValue;
		};
		textOffsetForReadOnlyWithLeftIcon: {
			paddingLeft: CssValue;
		};
	};
};

export const sizeWithTextVariantMapping: ISizeWithTextVariantMapping = {
	[StringInputV2Size.small]: {
		labelTextVariant: 'sans/09/400',
		inputIconSize: {
			width: `1rem`,
			height: `1rem`,
		},
		hintIconSize: { width: `0.75rem`, height: `0.75rem` },
		searchSelectTextBox: { gap: `0.25rem`, padding: `0.5rem`, fontSize: `11px` },
		inputFieldHeight: `2rem`,
		hint: {
			gap: `0.25rem`,
			marginTop: `0.25rem`,
		},
		label: { marginBottom: `0.25rem` },
		leftIconInputPadding: { paddingLeft: `2rem` },
		textOffsetForReadOnlyWithLeftIcon: {
			paddingLeft: `2rem`,
		},
	},
	[StringInputV2Size.medium]: {
		labelTextVariant: 'sans/14/400',
		inputIconSize: {
			width: `1.5rem`,
			height: `1.5rem`,
		},
		hintIconSize: { width: `1rem`, height: `1rem` },
		searchSelectTextBox: { gap: `0.5rem`, padding: `0.5rem`, fontSize: `17px` },
		inputFieldHeight: `2rem`,
		hint: {
			gap: `0.25rem`,
			marginTop: `0.5rem`,
		},
		label: { marginBottom: `0.5rem` },
		leftIconInputPadding: { paddingLeft: `2.5rem` },
		textOffsetForReadOnlyWithLeftIcon: {
			paddingLeft: `2rem`,
		},
	},
	[StringInputV2Size.s]: {
		labelTextVariant: 'sans/09/400',
		inputIconSize: {
			width: `1rem`,
			height: `1rem`,
		},
		hintIconSize: { width: `0.75rem`, height: `0.75rem` },
		searchSelectTextBox: { gap: `0.25rem`, padding: `0.5rem`, fontSize: `11px` },
		inputFieldHeight: '2rem',
		hint: {
			gap: `0.25rem`,
			marginTop: `0.25rem`,
		},
		label: { marginBottom: `0.25rem` },
		leftIconInputPadding: { paddingLeft: `2rem` },
		textOffsetForReadOnlyWithLeftIcon: {
			paddingLeft: `2rem`,
		},
	},
	[StringInputV2Size.m]: {
		labelTextVariant: 'sans/14/400',
		inputIconSize: {
			width: `1.5rem`,
			height: `1.5rem`,
		},
		hintIconSize: { width: `1rem`, height: `1rem` },
		searchSelectTextBox: { gap: `0.5rem`, padding: `0.5rem`, fontSize: `14px` },
		inputFieldHeight: '2.5rem',
		hint: {
			gap: `0.25rem`,
			marginTop: `0.25rem`,
		},
		label: { marginBottom: `0.5rem` },
		leftIconInputPadding: { paddingLeft: `2.5rem` },
		textOffsetForReadOnlyWithLeftIcon: {
			paddingLeft: `2rem`,
		},
	},
	[StringInputV2Size.l]: {
		labelTextVariant: 'sans/14/400',
		inputIconSize: {
			width: `2rem`,
			height: `2rem`,
		},
		hintIconSize: { width: `1rem`, height: `1rem` },
		searchSelectTextBox: { gap: `0.5rem`, padding: `0.5rem`, fontSize: `17px` },
		inputFieldHeight: '3rem',
		hint: {
			gap: `0.25rem`,
			marginTop: `0.5rem`,
		},
		label: { marginBottom: `0.5rem` },
		leftIconInputPadding: { paddingLeft: `3rem` },
		textOffsetForReadOnlyWithLeftIcon: {
			paddingLeft: `2.5rem`,
		},
	},
	[StringInputV2Size.search]: {
		labelTextVariant: 'sans/14/400',
		inputIconSize: {
			width: `1rem`,
			height: `1rem`,
		},
		hintIconSize: { width: `1rem`, height: `1rem` },
		searchSelectTextBox: { gap: `0.5rem`, padding: `0.5rem`, fontSize: `17px` },
		inputFieldHeight: '3rem',
		hint: {
			gap: `0.25rem`,
			marginTop: `0.5rem`,
		},
		label: { marginBottom: `0.5rem` },
		leftIconInputPadding: { paddingLeft: `2rem` },
		textOffsetForReadOnlyWithLeftIcon: {
			paddingLeft: `2.5rem`,
		},
	},
};

const StringInputV2 = ({
	className,
	onInputChange,
	label,
	hint,
	leftIcon,
	rightIcon,
	hintIcon,
	size = 'small',
	inputPlaceHolder = '...',
	inputType,
	latestInputValue,
	readOnly,
	name,
	rhfOptions,
	blockRedundantSpaces,
	disabled,
	autocompleteEnabled,
	onClick,
	hotkeyComponent,
	inputRef,
	'data-testid': dataTestId,
}: Props) => {
	const [value, setValue] = useState(latestInputValue ?? '');
	const textInputRef = useRef<HTMLInputElement | null>(null);
	const onValueChange = useMemo(() => debounce(onInputChange, 500), [onInputChange]);
	const handleChange = (event: BaseSyntheticEvent) => {
		const onChangeInput = event.target.value;
		setValue(onChangeInput);
		onValueChange(onChangeInput);
	};
	const {
		field,
		fieldState: { error: fieldStateError },
	} = useController({ name, rules: rhfOptions });

	useEffect(() => {
		if (!latestInputValue) {
			setValue(field.value ?? '');
		} else {
			setValue(latestInputValue);
		}
	}, [field.value, latestInputValue]);

	const LabelComponent = useMemo(() => {
		return typeof label === 'string' ? (
			<LabelWrapper>
				<Text
					className={className}
					variant={
						sizeWithTextVariantMapping[size].labelTextVariant as PredefinedTextVariants
					}
					disableVariantMapping
					key={size}
				>
					{label}
				</Text>
			</LabelWrapper>
		) : (
			label
		);
	}, [size, label]);
	const IsIconAString = (icon: string | React.ReactNode) => {
		return typeof icon === 'string';
	};

	const IconTypes = {
		leftIcon: <LeftIcon variant={leftIcon as PredefinedIcons} />,
		rightIcon: <RightIcon variant={rightIcon as PredefinedIcons} />,
		hintIcon: <HintIcon variant={hintIcon as PredefinedIcons} />,
	};

	const HintComponent = ({ error }: { error?: FieldError }) => {
		if (typeof hint !== 'string' && !error) {
			return <div>{hint}</div>;
		}
		const isErrored = !!error;
		return (
			<UIElementIdentifier
				identifier={INVESTOR_TEST_ELEMENT_ID.INVESTOR_GROUP_WRAPPER_ERROR_TEXT}
			>
				<HintWrapper className={className} key={size} isErrored={isErrored}>
					{hintIcon && IsIconAString(hintIcon) ? IconTypes.hintIcon : hintIcon}
					<HintText
						variant={
							sizeWithTextVariantMapping[size]
								.labelTextVariant as PredefinedTextVariants
						}
						disableVariantMapping
					>
						{error?.message ? error.message : hint}
					</HintText>
				</HintWrapper>
			</UIElementIdentifier>
		);
	};
	const isFieldStateError = !!fieldStateError;
	const shouldShowHint = hint || fieldStateError;
	const inputLength = leftIcon
		? field?.value?.toString().length + sizeWithTextVariantMapping[size].inputIconSize
		: field?.value?.toString().length;

	return (
		<div className={className}>
			{label && LabelComponent}
			<SearchSelectTextbox
				isErrored={isFieldStateError}
				length={inputLength}
				readOnly={readOnly}
				onClick={onClick}
				size={size}
				hotkeyComponent={hotkeyComponent}
			>
				{leftIcon && IsIconAString(leftIcon) ? IconTypes.leftIcon : leftIcon}

				<InputText
					data-testid={dataTestId}
					placeholder={inputPlaceHolder}
					onInput={handleChange}
					type={inputType}
					id="string-input-id"
					disabled={disabled || readOnly}
					autoComplete={autocompleteEnabled ? 'new-password' : 'off'}
					{...field}
					value={value}
					ref={(e) => {
						field.ref(e);
						textInputRef.current = e;

						if (inputRef) {
							inputRef.current = e;
						}
					}}
					onChange={(event) => {
						const value = event.target.value;
						if (!blockRedundantSpaces) {
							return field.onChange(value);
						}

						//d.zghinnik 24.01.2023 MIP-226
						//replace multiple spaces with one, remove first space
						const preparedValue = value.replace(/\s\s+/g, ' ').replace(/^\s/, '');
						field.onChange(preparedValue);
					}}
				/>

				{rightIcon && IsIconAString(rightIcon) ? IconTypes.rightIcon : rightIcon}
				{hotkeyComponent && <div className="hotkey">{hotkeyComponent}</div>}
			</SearchSelectTextbox>
			{shouldShowHint && <HintComponent error={fieldStateError} />}
		</div>
	);
};

const LabelWrapper = styled.div``;
const HintWrapper = styled.div<{ isErrored: boolean }>`
	display: flex;
	flex-direction: row;
	align-items: center;
	margin-top: ${em(4)};
	${({ isErrored }) =>
		isErrored &&
		css`
			${HintText} {
				color: ${({ theme }) => theme.v2.color.error[800]};
			}
		`}
`;
const LeftIcon = styled(Icon)``;
export const RightIcon = styled(Icon)``;
const HintIcon = styled(Icon)``;
const HintText = styled(Text)`
	color: var(--default-input-font-color);
`;
const InputText = styled.input.attrs(({ type }) => ({
	type: type || 'text',
}))`
	border-radius: ${em(4)};
`;

export const SearchSelectTextbox = styled.div<{
	isErrored?: boolean;
	length: number;
	readOnly?: boolean;
	size: StringInputV2SizeValues;
	hotkeyComponent?: React.ReactNode;
}>`
	display: flex;
	align-items: center;
	position: relative;
	gap: ${em(8)};

	${InputText} {
		width: ${({ length, readOnly }) => (readOnly && length !== 0 ? `${length + 2}ch` : '100%')};
		border: solid 1px
			${({ theme, isErrored }) =>
				isErrored ? theme.v2.color.error[800] : theme.v2.color.neutral[300]};
		border-radius: ${em(4)};
		background-color: ${({ theme, isErrored }) =>
			isErrored ? theme.v2.color.error[400] : theme.v2.color.white[500]};
		::placeholder {
			color: ${({ theme, isErrored }) =>
				isErrored ? theme.v2.color.neutral[700] : css`var(--default-input-font-color)`};
		}
		box-sizing: border-box;
		height: 100%;

		${({ hotkeyComponent }) => !!hotkeyComponent && `padding-right: 4rem !important;`};
	}

	${Icon} {
		position: absolute;
	}

	box-sizing: border-box;
	height: ${({ size }) => sizeWithTextVariantMapping[size].inputFieldHeight};
`;

const Styled = styled(StringInputV2)`
	--default-input-font-color: ${({ theme }) => theme.v2.color.neutral[500]};
	--black-color: ${({ theme }) => theme.v2.color.black[500]};
	--default-font-color: ${({ theme }) => theme.v2.color.neutral[1100]};
	color: var(--default-font-color);
	${Icon} {
		color: var(--black-color);
	}
	${InputText} {
		&:focus {
			outline: solid 1px var(--default-input-font-color);
			border: solid 1px ${({ theme }) => theme.v2.color.primary[800]};
		}
		&:hover {
			border: solid 1px ${({ theme }) => theme.v2.color.neutral[700]};
		}
		&:active {
			::placeholder {
				color: var(--default-font-color);
			}
			color: var(--default-font-color);
			border: solid 1px ${({ theme }) => theme.v2.color.secondary[800]};
		}
	}

	${({ rightIcon }) =>
		rightIcon &&
		`${RightIcon} {
            right: ${em(8)};
        }
    `};

	${({ leftIcon, size = 'small', readOnly }) =>
		leftIcon &&
		`${LeftIcon} {
            left: ${!readOnly && em(8)};
        };
        ${InputText} {
           padding-left:  ${!readOnly && sizeWithTextVariantMapping[size].leftIconInputPadding.paddingLeft} !important;
        };
    `};

	${({ size = 'small' }) =>
		size &&
		css`
			${HintIcon} {
				${sizeWithTextVariantMapping[size].hintIconSize};
			}

			${LeftIcon},${RightIcon} {
				${sizeWithTextVariantMapping[size].inputIconSize};
			}
			${HintWrapper} {
				${sizeWithTextVariantMapping[size].hint};
			}
			${LabelWrapper} {
				${sizeWithTextVariantMapping[size].label};
			}
			${InputText} {
				${sizeWithTextVariantMapping[size].searchSelectTextBox}
			}
		`};

	${({ disabled }) =>
		disabled &&
		css`
			${InputText} {
				border: solid 1px ${({ theme }) => theme.v2.color.neutral[500]};
				background-color: ${({ theme }) => theme.v2.color.neutral[300]};
			}
		`};

	${({ readOnly, leftIcon, size }) =>
		readOnly &&
		css`
			${LabelWrapper} {
				margin-bottom: ${em(8)};
			}
			#string-input-id {
				width: 100%;
				border: solid 1px transparent;
				color: ${({ theme }) => theme.v2.color.neutral[1100]};
				background-color: transparent;
				padding-left: ${leftIcon && size
					? sizeWithTextVariantMapping[size].textOffsetForReadOnlyWithLeftIcon.paddingLeft
					: '0'};
			}
		`};

	.hotkey {
		position: absolute;
		top: 50%;
		right: 0.5rem;
		transform: translateY(-50%);
	}
`;

export { Styled as StringInput };
